import { cn } from '@/lib/utils'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { fadeIn, staggerContainer } from '@/v2/constants/motion'
import { FaqQuestionType } from '@/v2/types'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import { useState } from 'react'

interface FaqSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  description?: string
  questions: FaqQuestionType[]
  isBgDark?: boolean
  sectionTitleClassName?: string
}

const FaqSection: React.FC<FaqSectionProps> = props => {
  const { title, description, questions, className, isBgDark = false, sectionTitleClassName, ...rest } = props
  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  const [activeFaq, setActiveFaq] = useState<number | null>(0)

  const handleActiveFaq = (id: number) => {
    setActiveFaq(prevActiveFaq => (prevActiveFaq === id ? null : id))
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer(0.5, 0)}
        initial='hidden'
        whileInView='show'
        viewport={{ once: true, amount: 0.25 }}
        className={cn('text-center landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionHead title={title} description={description} className={sectionTitleClassName} />
        <SectionWrapper {...rest} className='xl:max-w-[1100px]'>
          {/* questions & answers */}
          <div className='flex flex-col gap-4'>
            {questions?.map((faq, index) => {
              const isActive = activeFaq === index

              return (
                <m.article
                  key={faq.question}
                  className={cn(
                    'cursor-pointer p-5 rounded-[10px] bg-[#F9F8F8] text-left border-2 transition-all [&>h3]:font-semibold [&>h3]:xl-min:text-xl [&>h3]:text-lg [&>h3]:flex [&>h3]:items-center [&>h3]:justify-between',
                    isActive ? 'border-[#0A9CFF] [&>h3]:mb-3' : 'border-[#E5E5E5]',
                    cardBackground
                  )}
                  variants={fadeIn('up', 'tween', 0.5, 1)}
                  onClick={() => handleActiveFaq(index)}
                >
                  <h3>
                    {faq.question}
                    <svg
                      className='flex-shrink-0'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      style={{
                        transition: 'transform 0.3s ease, fill 0.3s ease',
                        transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                        fill: isActive ? '#FF6D0A' : '#D3D3D3'
                      }}
                    >
                      <polygon points='12,18 4,6 20,6' />
                    </svg>
                  </h3>
                  <p
                    className={cn(
                      `transition-all duration-500 ease-in-out text-base overflow-hidden text-paragraph md-mn:w-[80%] leading-6`,
                      isActive ? 'md-mn:max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    )}
                  >
                    {faq.answer}
                  </p>

                  {faq.points && (
                    <ul
                      className={cn(
                        'list-outside list-disc ml-7 mt-2 space-y-1 lg-mn:w-[70%]',
                        isActive ? 'md-mn:max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      )}
                    >
                      {faq.points.map((point, pointIdx) => (
                        <li
                          key={pointIdx}
                          dangerouslySetInnerHTML={{ __html: point }}
                          className='text-paragraph font-normal leading-6 text-base break-words'
                        />
                      ))}
                    </ul>
                  )}
                </m.article>
              )
            })}
          </div>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default FaqSection
